import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  FileText, AlertTriangle, Leaf, ArrowLeft, Copy, Printer,
  Thermometer, Clock, Droplets, Calendar, CheckCircle, 
  RefreshCw, ChevronDown, ChevronUp, Activity, Heart,
  Share2, Bookmark, BookmarkCheck, Link2, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecipeDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [recipe, setRecipe] = useState(null);
  const [editorialMode, setEditorialMode] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [shareCode, setShareCode] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    clinical: true,
    practical: true,
    substitutions: false
  });

  useEffect(() => {
    // Check if loading from share code
    const sharedCode = searchParams.get('share');
    if (sharedCode) {
      loadSharedRecipe(sharedCode);
    } else {
      // Load recipe from localStorage
      const savedRecipe = localStorage.getItem('acupedia_current_recipe');
      if (savedRecipe) {
        setRecipe(JSON.parse(savedRecipe));
        checkIfSaved(JSON.parse(savedRecipe));
      } else {
        toast.error('Rețeta nu a fost găsită');
        navigate('/results');
      }
    }
  }, [id, navigate, searchParams]);

  const loadSharedRecipe = async (code) => {
    try {
      const response = await axios.get(`${API}/recipes/shared/${code}`);
      setRecipe(response.data.recipe_data);
      setShareCode(code);
      toast.success('Rețetă partajată încărcată!');
    } catch (error) {
      toast.error('Link-ul de partajare nu este valid sau a expirat');
      navigate('/');
    }
  };

  const checkIfSaved = (recipeData) => {
    const savedRecipes = JSON.parse(localStorage.getItem('acupedia_saved_recipes') || '[]');
    const found = savedRecipes.find(r => r.name === recipeData.name);
    setIsSaved(!!found);
    if (found) setShareCode(found.share_code);
  };

  const handleCopyRecipe = () => {
    if (!recipe) return;

    const text = `
${recipe.name}
================
${recipe.scop}

INGREDIENTE:
${recipe.ingredients.map(ing => `• ${ing.herb_name}: ${ing.grams}g (${ing.household_measure})`).join('\n')}

PREPARARE:
• Apă: ${recipe.water_ml} ml
• Temperatură: ${recipe.temperature}
• Timp infuzie: ${recipe.infusion_time_min} minute
• Metodă: ${recipe.infusion_type}

ADMINISTRARE:
• Când: ${recipe.administration?.when || 'Conform preferințelor'}
• Frecvență: ${recipe.administration?.frequency || 'De 1-2 ori pe zi'}
• Durată: ${recipe.administration?.duration || '5-7 zile'}
${recipe.administration?.avoid ? `• De evitat: ${recipe.administration.avoid}` : ''}

${recipe.cald_rece_recommendation}

DISCLAIMER: Acest conținut are scop educativ și nu înlocuiește consultul medical.
    `.trim();

    navigator.clipboard.writeText(text).then(() => {
      toast.success('Rețeta a fost copiată!');
    }).catch(() => {
      toast.error('Nu s-a putut copia rețeta');
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSaveRecipe = async () => {
    if (!recipe) return;
    setSaving(true);
    
    try {
      const response = await axios.post(`${API}/recipes/save`, {
        recipe_data: recipe,
        user_id: 'anonymous'
      });
      
      // Save locally too
      const savedRecipes = JSON.parse(localStorage.getItem('acupedia_saved_recipes') || '[]');
      savedRecipes.push({
        ...recipe,
        share_code: response.data.share_code,
        saved_at: new Date().toISOString()
      });
      localStorage.setItem('acupedia_saved_recipes', JSON.stringify(savedRecipes));
      
      setIsSaved(true);
      setShareCode(response.data.share_code);
      toast.success('Rețeta a fost salvată!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Eroare la salvarea rețetei');
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    if (!shareCode) {
      // Save first to get share code
      handleSaveRecipe().then(() => {
        setShowShareDialog(true);
      });
    } else {
      setShowShareDialog(true);
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/recipe/shared?share=${shareCode}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('Link copiat în clipboard!');
    }).catch(() => {
      toast.error('Nu s-a putut copia link-ul');
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!recipe) {
    return (
      <div className="py-16 text-center">
        <div className="skeleton h-96 max-w-3xl mx-auto rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12 animate-fade-in print-container">
      <div className="container-acupedia max-w-4xl">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 no-print">
          <Button
            variant="ghost"
            onClick={() => navigate('/results')}
            className="self-start"
            data-testid="back-results-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la rezultate
          </Button>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleSaveRecipe}
              disabled={saving || isSaved}
              className={`${isSaved ? 'bg-acupedia-gold/10 text-acupedia-gold border-acupedia-gold' : ''}`}
              data-testid="save-recipe-btn"
            >
              {isSaved ? (
                <>
                  <BookmarkCheck className="w-4 h-4 mr-2" />
                  Salvată
                </>
              ) : saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <Bookmark className="w-4 h-4 mr-2" />
                  Salvează
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleShare}
              className="bg-acupedia-teal/10 text-acupedia-teal border-acupedia-teal/20 hover:bg-acupedia-teal/20"
              data-testid="share-recipe-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Împărtășește
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyRecipe}
              className="copy-btn"
              data-testid="copy-recipe-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiază
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="print-btn"
              data-testid="print-recipe-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Editorial Mode Toggle */}
        <div className="editorial-toggle mb-8 no-print">
          <Switch
            id="editorial-mode"
            checked={editorialMode}
            onCheckedChange={setEditorialMode}
            data-testid="editorial-mode-switch"
          />
          <Label htmlFor="editorial-mode" className="cursor-pointer">
            <span className="font-medium">Mod editorial</span>
            <span className="text-sm text-stone-500 ml-2">(ca în fișele seriei)</span>
          </Label>
        </div>

        {/* Main Recipe Card */}
        <div className="recipe-card card-recipe">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-stone-200">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <Badge className="mb-3 bg-acupedia-teal/10 text-acupedia-teal border-0">
                  Rețetă Ancoră
                </Badge>
                <h1 className="font-heading text-3xl md:text-4xl text-acupedia-teal mb-3">
                  {recipe.name}
                </h1>
              </div>
              <Badge className={`flex-shrink-0 ${
                recipe.match_score >= 70 ? 'bg-green-100 text-green-800' :
                recipe.match_score >= 50 ? 'bg-amber-100 text-amber-800' :
                'bg-stone-100 text-stone-700'
              }`}>
                {recipe.match_score}% potrivire
              </Badge>
            </div>
            <p className="text-lg text-stone-600">{recipe.scop}</p>
          </div>

          {editorialMode ? (
            /* Editorial Mode Layout */
            <div className="space-y-8">
              {/* Page 1 Content */}
              <section>
                <h2 className="font-heading text-2xl text-acupedia-teal mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Pagina 1 – Text complet
                </h2>
                
                {/* Full Recipe Content in Editorial Style */}
                <div className="prose prose-stone max-w-none">
                  <p className="text-stone-700 leading-relaxed mb-6">
                    {recipe.scop} Această formulă acționează pe meridianele {recipe.meridians?.join(', ')}, 
                    cu o direcție energetică de {recipe.energetic_direction}. 
                  </p>

                  {/* Clinical Notes in Editorial */}
                  <div className="bg-acupedia-teal/5 p-6 rounded-xl mb-6">
                    <h3 className="font-heading text-xl text-acupedia-teal mb-4">Note clinice</h3>
                    <ul className="space-y-2">
                      {recipe.why_it_works?.map((reason, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-700">
                          <CheckCircle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                          {reason}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ingredients */}
                  <h3 className="font-heading text-xl text-stone-800 mb-4">Ingrediente</h3>
                  <div className="grid gap-3 mb-6">
                    {recipe.ingredients.map((ing, i) => (
                      <div key={i} className="ingredient-item bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Leaf className="w-5 h-5 text-acupedia-teal" />
                          <span className="font-medium text-stone-800">{ing.herb_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-acupedia-teal font-semibold">{ing.grams}g</span>
                          <span className="text-sm text-stone-500 ml-2">({ing.household_measure})</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Administration */}
                  <h3 className="font-heading text-xl text-stone-800 mb-4">Administrare</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg">
                      <Clock className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-stone-800">Când</span>
                        <p className="text-sm text-stone-600">{recipe.administration?.when}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg">
                      <RefreshCw className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-stone-800">Frecvență</span>
                        <p className="text-sm text-stone-600">{recipe.administration?.frequency}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-stone-800">Durată</span>
                        <p className="text-sm text-stone-600">{recipe.administration?.duration}</p>
                      </div>
                    </div>
                    {recipe.administration?.avoid && (
                      <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-amber-800">De evitat</span>
                          <p className="text-sm text-amber-700">{recipe.administration.avoid}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Disclaimer */}
                  <div className="medical-disclaimer">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                      <p className="text-sm">
                        <strong>Disclaimer Medical:</strong> Acest conținut are scop educativ și nu înlocuiește consultul medical. 
                        Dacă simptomele persistă peste 72h, aveți febră, dificultăți de respirație, sânge sau scădere în greutate, 
                        consultați imediat un medic.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Page 2 - Quick Card */}
              <section className="border-t-2 border-acupedia-gold pt-8">
                <h2 className="font-heading text-2xl text-acupedia-teal mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Pagina 2 – Rezumat practic
                </h2>
                
                <div className="bg-white border-2 border-acupedia-teal rounded-xl p-6 shadow-lg">
                  <h3 className="font-heading text-2xl text-center text-acupedia-teal mb-6">{recipe.name}</h3>
                  
                  {/* Quick Ingredients */}
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {recipe.ingredients.map((ing, i) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-stone-50 rounded">
                        <span className="text-sm">{ing.herb_name}</span>
                        <span className="font-mono text-sm text-acupedia-teal">{ing.grams}g</span>
                      </div>
                    ))}
                  </div>

                  {/* Quick Method */}
                  <div className="text-center mb-6 p-4 bg-acupedia-teal/5 rounded-lg">
                    <p className="font-mono text-lg">
                      {recipe.water_ml}ml • {recipe.temperature} • {recipe.infusion_time_min} min
                    </p>
                  </div>

                  {/* Quick Admin */}
                  <p className="text-center text-stone-600 mb-4">
                    {recipe.administration?.when} • {recipe.administration?.frequency} • {recipe.administration?.duration}
                  </p>

                  {/* Meridians */}
                  <div className="flex flex-wrap justify-center gap-2">
                    {recipe.meridians?.map((m, i) => (
                      <span key={i} className="meridian-badge">{m}</span>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          ) : (
            /* Standard Mode Layout */
            <>
              {/* A) Scop & Context - Already shown in header */}

              {/* B) Rețeta Ancoră */}
              <section className="recipe-section">
                <h2 className="recipe-section-title">
                  <Leaf className="w-5 h-5" />
                  Ingrediente
                </h2>
                <div className="space-y-3">
                  {recipe.ingredients.map((ing, i) => (
                    <div key={i} className="ingredient-item bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-acupedia-teal/10 flex items-center justify-center">
                          <Leaf className="w-4 h-4 text-acupedia-teal" />
                        </div>
                        <span className="ingredient-name">{ing.herb_name}</span>
                      </div>
                      <div className="text-right">
                        <span className="ingredient-amount text-lg">{ing.grams}g</span>
                        <span className="text-sm text-stone-500 ml-2">({ing.household_measure})</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Preparation Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                  <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                    <Droplets className="w-6 h-6 text-acupedia-teal mx-auto mb-2" />
                    <span className="font-mono text-lg text-acupedia-teal">{recipe.water_ml} ml</span>
                    <p className="text-xs text-stone-500 mt-1">apă</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                    <Thermometer className="w-6 h-6 text-acupedia-teal mx-auto mb-2" />
                    <span className="text-sm text-acupedia-teal">{recipe.temperature}</span>
                    <p className="text-xs text-stone-500 mt-1">temperatură</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                    <Clock className="w-6 h-6 text-acupedia-teal mx-auto mb-2" />
                    <span className="font-mono text-lg text-acupedia-teal">{recipe.infusion_time_min} min</span>
                    <p className="text-xs text-stone-500 mt-1">infuzie</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                    <Activity className="w-6 h-6 text-acupedia-teal mx-auto mb-2" />
                    <span className="text-sm text-acupedia-teal capitalize">{recipe.infusion_type}</span>
                    <p className="text-xs text-stone-500 mt-1">metodă</p>
                  </div>
                </div>
              </section>

              {/* C) Administrare */}
              <section className="recipe-section">
                <h2 className="recipe-section-title">
                  <Calendar className="w-5 h-5" />
                  Metodă de consum
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                    <Clock className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-stone-800">Când să bei</span>
                      <p className="text-sm text-stone-600 mt-1">{recipe.administration?.when}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                    <RefreshCw className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-stone-800">Frecvență</span>
                      <p className="text-sm text-stone-600 mt-1">{recipe.administration?.frequency}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                    <Calendar className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-stone-800">Durată recomandată</span>
                      <p className="text-sm text-stone-600 mt-1">{recipe.administration?.duration}</p>
                    </div>
                  </div>
                  {recipe.administration?.avoid && (
                    <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-amber-800">Când NU e recomandat</span>
                        <p className="text-sm text-amber-700 mt-1">{recipe.administration.avoid}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* D) Cald vs Rece */}
              <section className="recipe-section">
                <h2 className="recipe-section-title">
                  <Thermometer className="w-5 h-5" />
                  Cald vs Rece
                </h2>
                <div className={`p-4 rounded-lg ${
                  recipe.cald_rece_recommendation?.includes('CALD') ? 'bg-orange-50 border border-orange-200' :
                  recipe.cald_rece_recommendation?.includes('RECE') ? 'bg-cyan-50 border border-cyan-200' :
                  'bg-stone-50 border border-stone-200'
                }`}>
                  <p className={`font-medium mb-2 ${
                    recipe.cald_rece_recommendation?.includes('CALD') ? 'text-orange-800' :
                    recipe.cald_rece_recommendation?.includes('RECE') ? 'text-cyan-800' :
                    'text-stone-800'
                  }`}>
                    {recipe.cald_rece_recommendation}
                  </p>
                  {recipe.cold_infusion_method && (
                    <p className="text-sm text-stone-600 mt-2">
                      <strong>Metodă infuzie la rece:</strong> {recipe.cold_infusion_method}
                    </p>
                  )}
                </div>
              </section>

              {/* E) Substituții */}
              {recipe.substitutions?.length > 0 && (
                <Collapsible open={expandedSections.substitutions} onOpenChange={() => toggleSection('substitutions')}>
                  <section className="recipe-section">
                    <CollapsibleTrigger className="w-full">
                      <h2 className="recipe-section-title justify-between cursor-pointer">
                        <span className="flex items-center gap-2">
                          <RefreshCw className="w-5 h-5" />
                          Substituții din kit
                        </span>
                        {expandedSections.substitutions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </h2>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-2 mt-4">
                        {recipe.substitutions.map((sub, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                            <span className="text-stone-500">{sub.original}</span>
                            <span className="text-acupedia-gold">→</span>
                            <span className="font-medium text-acupedia-teal">{sub.replacement}</span>
                            <span className="text-xs text-stone-400">({sub.note})</span>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </section>
                </Collapsible>
              )}

              {/* F) Upgrade Suggestions */}
              {recipe.upgrades?.length > 0 && (
                <section className="recipe-section">
                  <h2 className="recipe-section-title">
                    <Heart className="w-5 h-5" />
                    Sugestii de upgrade
                  </h2>
                  <ul className="space-y-2">
                    {recipe.upgrades.map((upgrade, i) => (
                      <li key={i} className="flex items-start gap-2 text-stone-600">
                        <CheckCircle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                        {upgrade}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* G) Note de bun-simț */}
              <Collapsible open={expandedSections.practical} onOpenChange={() => toggleSection('practical')}>
                <section className="recipe-section">
                  <CollapsibleTrigger className="w-full">
                    <h2 className="recipe-section-title justify-between cursor-pointer">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Note de bun-simț
                      </span>
                      {expandedSections.practical ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </h2>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="space-y-2 mt-4">
                      {recipe.practical_notes?.map((note, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-600">
                          <CheckCircle className="w-4 h-4 text-acupedia-gold flex-shrink-0 mt-1" />
                          {note}
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </section>
              </Collapsible>

              {/* Clinical Notes */}
              <Collapsible open={expandedSections.clinical} onOpenChange={() => toggleSection('clinical')}>
                <section className="recipe-section bg-acupedia-teal/5 -mx-8 px-8 py-6 rounded-xl">
                  <CollapsibleTrigger className="w-full">
                    <h2 className="recipe-section-title justify-between cursor-pointer text-acupedia-teal">
                      <span className="flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        Note clinice & Meridiane
                      </span>
                      {expandedSections.clinical ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </h2>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-4 space-y-4">
                      {/* Meridians */}
                      <div>
                        <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-2">
                          Meridiane energetice vizate
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {recipe.meridians?.map((m, i) => (
                            <span key={i} className="meridian-badge">{m}</span>
                          ))}
                        </div>
                      </div>

                      {/* Energetic Direction */}
                      <div>
                        <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-2">
                          Direcția energetică
                        </h3>
                        <p className="text-stone-700">{recipe.energetic_direction}</p>
                      </div>

                      {/* Why it works */}
                      <div>
                        <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-2">
                          De ce funcționează
                        </h3>
                        <ul className="space-y-2">
                          {recipe.why_it_works?.map((reason, i) => (
                            <li key={i} className="flex items-start gap-2 text-stone-700">
                              <CheckCircle className="w-4 h-4 text-acupedia-gold flex-shrink-0 mt-1" />
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Success Signs */}
                      {recipe.success_signs?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-2">
                            Semne că ai ales bine rețeta
                          </h3>
                          <ul className="space-y-2">
                            {recipe.success_signs.map((sign, i) => (
                              <li key={i} className="flex items-start gap-2 text-stone-700">
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                                {sign}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>

              {/* H) Medical Disclaimer */}
              <section className="mt-8">
                <div className="medical-disclaimer">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-stone-800 mb-1">Disclaimer Medical</p>
                      <p className="text-sm text-stone-600">
                        Acest conținut are scop educativ și nu înlocuiește consultul medical. 
                        <strong> Dacă simptomele persistă peste 72h, aveți febră, dificultăți de respirație, 
                        sânge sau scădere în greutate, consultați imediat un medic.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Share2 className="w-5 h-5 text-acupedia-teal" />
                Împărtășește Rețeta
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-stone-600 text-sm">
                Trimite acest link prietenilor sau familiei pentru a le împărtăși rețeta:
              </p>
              
              <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <Link2 className="w-5 h-5 text-stone-400 flex-shrink-0" />
                <code className="text-sm text-acupedia-teal flex-1 truncate">
                  {shareCode ? `${window.location.origin}/recipe/shared?share=${shareCode}` : 'Se generează...'}
                </code>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={copyShareLink}
                  className="btn-primary"
                  disabled={!shareCode}
                  data-testid="copy-share-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiază link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const text = `Am descoperit o rețetă de ceai terapeutic pe Acupedia! 🍵\n\n${recipe?.name}\n\n${window.location.origin}/recipe/shared?share=${shareCode}`;
                    if (navigator.share) {
                      navigator.share({
                        title: recipe?.name,
                        text: text,
                        url: `${window.location.origin}/recipe/shared?share=${shareCode}`
                      });
                    } else {
                      navigator.clipboard.writeText(text);
                      toast.success('Text copiat pentru partajare!');
                    }
                  }}
                  disabled={!shareCode}
                  data-testid="native-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Partajează
                </Button>
              </div>

              <p className="text-xs text-stone-500 text-center">
                Link-ul poate fi accesat de oricine pentru a vedea rețeta.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
